'use client';
import { FetchUtilities } from '@/lib/platform/actions/fetch-utilities';
import {
  addToast,
  Autocomplete,
  AutocompleteItem,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  Select,
  SelectItem,
  useDisclosure,
} from '@heroui/react';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Utility } from '../types/utility-type';
import useEegDataStore, { EegDataState } from '../store/use-eeg-data-store';
import useEegUserDataStore from '@/app/store/use-eeg-user-data-store';
import useEnergyCalculatorStore from '@/app/store/use-energy-calculator-store';
import { fetchAndUpdateOffer } from '@/app/store/use-offer-comparison-store';
import { EegUserDataLocation } from '@/lib/eeg/types/user-data';
import { ReferenceTariffType } from '@/app/types/reference-tariff-type';
import { getGridOperators } from '@/lib/offer-service/actions/get-grid-operators';
import { GridOperator } from '@/lib/offer-service/types/grid-operator';
import { getSize } from '@/lib/offer-service/types/customer-type';

export default function EegDataPopup() {
  const [isZipFieldVisible, setIsZipFieldVisible] = useState<boolean>(false);
  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const t = useTranslations('eegData');
  const isEegUser = useEegUserDataStore((state) => !!state.userData);
  const locations = useEegUserDataStore((state) => state.userData?.Locations);
  const inputFieldStyling = `bg-transparent border border-[var(--color-border-default)] rounded-lg h-[54px]`;
  const tryAgainMessage = t('tryAgain');
  const [utilities, setUtilities] = useState<Utility[]>([]);
  const { handleSubmit, control, watch, setValue, register } = useForm<{
    pod: string;
    utility: string;
    location?: EegUserDataLocation;
    zip?: string;
  }>({
    defaultValues: {
      // values load asynchronously, so we need to set defaults here
      pod: '',
      utility: '',
      location: undefined,
      zip: undefined,
    },
  });

  const selectedLocation = watch('location');
  const selectedPod = watch('pod');
  const pods = mapLocationToConsumptionPods(selectedLocation);
  const addressBuilder = (location: EegUserDataLocation) => {
    const zip = location.ZIP ?? '';
    return `${location.Street} ${location.StreetNo}, ${zip} ${location.City}, ${location.Country}`;
  };
  const userAddresses = locations ? locations.map(addressBuilder) : [];

  useEffect(() => {
    if (locations?.length && !selectedLocation) {
      setValue('location', locations[0], {
        shouldValidate: true,
        shouldDirty: true,
      });
      const locationPods = mapLocationToConsumptionPods(locations[0]);
      if (locationPods.length) {
        setValue('pod', locationPods[0], {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [locations, selectedLocation, setValue]);

  useEffect(() => {
    if (!selectedLocation) {
      return;
    }

    const hasZip = !!selectedLocation.ZIP;
    setIsZipFieldVisible(!hasZip);

    if (hasZip) {
      setValue('zip', '');
    }
  }, [selectedLocation, setValue]);

  useEffect(() => {
    if (isEegUser) {
      onOpen();
    }
  }, [isEegUser, onOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const fetchUtilities = async () => {
      try {
        const response = await FetchUtilities();
        if (response.error) {
          throw response.error;
        }
        const utilityList = response.data;
        if (!Array.isArray(utilityList)) {
          addToast({ title: tryAgainMessage });
          return;
        }
        const labeledUtilities = utilityList.map((utility) => ({
          key: utility,
          label: utility,
        }));
        setUtilities(labeledUtilities);
      } catch {
        addToast({ title: tryAgainMessage });
      }
    };
    fetchUtilities();
  }, [isOpen]);

  const onSubmit = (data: EegDataState & { zip?: string }) => {
    const getCalculation = async (
      selectedLocation: EegUserDataLocation,
      zipCode: string
    ) => {
      const consumption = Number(
        selectedLocation.Meters.find((m) => m.MeterNumber === data.pod)
          ?.Consumption
      );
      const customerType = useEnergyCalculatorStore.getState().customerType;

      if (selectedLocation) {
        let gridOperator: GridOperator | undefined;
        try {
          const gridOperators = await getGridOperators(zipCode);
          gridOperator = gridOperators[0];
        } catch (err) {
          console.error('Error fetching grid operators', err);
        }

        useEnergyCalculatorStore.setState({
          zipCode: zipCode,
          ...(consumption && {
            consumption: consumption,
            size: getSize(consumption, customerType),
          }),
          calculationMode: ReferenceTariffType.average,
          ...(gridOperator && {
            gridOperatorId: gridOperator.id,
            gridOperator: gridOperator.name,
            gridAreaId: gridOperator.gridAreaId,
          }),
        });

        return fetchAndUpdateOffer();
      }
    };

    if (selectedLocation && !selectedLocation.ZIP && data.zip) {
      selectedLocation.ZIP = data.zip;
    }

    const iban = selectedLocation?.Meters.find(
      (m) => m.MeterNumber === data.pod
    )?.Iban;

    useEegDataStore.setState({
      ...data,
      iban,
    });

    if (data.location && selectedLocation?.ZIP) {
      getCalculation(data.location, selectedLocation.ZIP);
    }
    onClose();
  };

  return (
    <Modal
      isDismissable={false}
      isKeyboardDismissDisabled={true}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="px-4 py-6 md:p-[100px]"
      size="2xl"
      classNames={{ closeButton: '!hidden' }}
    >
      <ModalContent>
        <ModalBody>
          <p
            className="font-medium leading-8.5 text-lg md:text-2xl"
            data-testid="eeg-popup-title"
          >
            {t('title')}
          </p>
          <form
            className="flex flex-col pb-8 pt-6 gap-y-6"
            onSubmit={handleSubmit(onSubmit)}
          >
            {locations && locations.length > 1 ? (
              <Controller
                control={control}
                name="location"
                render={({ field }) => (
                  <Select
                    data-testid="eeg-popup-location-selection"
                    isRequired
                    selectedKeys={
                      field.value ? [addressBuilder(field.value)] : []
                    }
                    onSelectionChange={(keySet) => {
                      const selectedAddress = Array.from(keySet)[0];
                      const loc = locations.find(
                        (l) => addressBuilder(l) === selectedAddress
                      );
                      field.onChange(loc);
                      const locationPods = mapLocationToConsumptionPods(loc);
                      if (!loc || !locationPods.length) {
                        setValue('pod', '');
                        return;
                      }

                      setValue('pod', locationPods[0]);
                    }}
                    classNames={{ label: 'pb-2', trigger: inputFieldStyling }}
                    labelPlacement="outside"
                    placeholder={userAddresses[0]}
                    label={t('address')}
                  >
                    {userAddresses.map((address: string) => (
                      <SelectItem
                        key={address}
                        data-testid="eeg-popup-location-input"
                      >
                        {address}
                      </SelectItem>
                    ))}
                  </Select>
                )}
              />
            ) : (
              <Input
                data-testid="eeg-popup-location-input"
                value={userAddresses[0]}
                label={t('address')}
                labelPlacement={'outside'}
                placeholder={userAddresses[0]}
                classNames={{
                  input: '!text-disabled',
                  inputWrapper: inputFieldStyling,
                  label: 'pb-2',
                }}
                isReadOnly
              />
            )}

            {isZipFieldVisible && (
              <Input
                type="tel"
                inputMode="numeric"
                maxLength={4}
                {...register('zip', { required: true, pattern: /^[0-9]+$/ })}
                data-testid="eeg-popup-zip-input"
                label={t('zip')}
                labelPlacement={'outside'}
                placeholder={t('zipPlaceholder')}
                classNames={{
                  inputWrapper: inputFieldStyling,
                  label: 'pb-2',
                }}
                errorMessage={<span className="pb-6">{t('zipError')}</span>}
                isRequired
              />
            )}

            {pods.length > 1 ? (
              <Controller
                control={control}
                name="pod"
                render={({ field }) => (
                  <Select
                    data-testid="eeg-popup-pod-selection"
                    isRequired
                    selectedKeys={field.value ? [field.value] : []}
                    onSelectionChange={(keySet) =>
                      field.onChange(Array.from(keySet)[0])
                    }
                    classNames={{ label: 'pb-2', trigger: inputFieldStyling }}
                    labelPlacement="outside"
                    placeholder={pods[0]}
                    label={t('pod')}
                  >
                    {pods.map((pod) => (
                      <SelectItem key={pod} data-testid="eeg-popup-pod-input">
                        {pod}
                      </SelectItem>
                    ))}
                  </Select>
                )}
              />
            ) : (
              <Input
                data-testid="eeg-popup-pod-input"
                value={selectedPod}
                label={t('pod')}
                labelPlacement={'outside'}
                placeholder={t('pod')}
                classNames={{
                  inputWrapper: inputFieldStyling,
                  label: 'pb-2',
                  input: '!text-disabled',
                }}
                isReadOnly
              />
            )}
            <Controller
              control={control}
              name="utility"
              rules={{ required: true }}
              render={({ field }) => (
                <Autocomplete
                  data-testid="eeg-popup-utility-selection"
                  isRequired
                  {...field}
                  placeholder={t('utilityPlaceholder')}
                  label={t('utility')}
                  labelPlacement="outside"
                  errorMessage={
                    <span className="pb-6">{t('utilityError')}</span>
                  }
                  variant="bordered"
                  onSelectionChange={field.onChange}
                >
                  {utilities.map((utility) => (
                    <AutocompleteItem
                      key={utility.key}
                      data-testid="eeg-popup-utility-input"
                    >
                      {utility.label}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              )}
            />
            <Button
              type="submit"
              data-testid="eeg-popup-submit"
              className="btn-padded btn-primary h-[50px] font-medium leading-6.5 w-full md:max-w-[150px]"
            >
              {t('submit')}
            </Button>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const mapLocationToConsumptionPods = (
  location?: EegUserDataLocation
): string[] =>
  location?.Meters.filter((meter) => meter.Type === 'consumption').map(
    (meter) => meter.MeterNumber
  ) ?? [];
